import React, { MouseEventHandler, useContext } from 'react';

import { locale as localeConfig } from '@adsk/au-common-js';

import { useUserProfile } from '../../../../apis/useUserSearch';
import Skeleton from '../../../../components/presentational/skeleton';
import { ConfigContext } from '../../../contexts/ConfigContext';
import I18nContext from '../../../contexts/I18nContext';

interface UserMenuListItem {
  readonly link?: string;
  readonly text: string;
  readonly onClick?: MouseEventHandler;
  readonly isLoading?: boolean;
}

interface MenuGroup {
  readonly list: UserMenuListItem[];
  readonly title: string;
}

interface MenuProps {
  readonly userId: string;
  readonly logoutOnClickOverride: (
    event: React.MouseEvent<HTMLButtonElement>
  ) => void;
}

function MenuLink({
  link,
  text,
  onClick,
  isLoading = false,
}: UserMenuListItem): JSX.Element | null {
  if (isLoading) {
    return <Skeleton width="100%" height={13} />;
  }
  if (onClick) {
    return (
      <button className="user-menu-link" onClick={onClick} type="button">
        {text}
      </button>
    );
  }
  if (link) {
    return (
      <a className="user-menu-link" href={link}>
        {text}
      </a>
    );
  }

  return null;
}

export function Menu({
  userId,
  logoutOnClickOverride,
}: MenuProps): JSX.Element {
  const { t, i18n } = useContext(I18nContext);
  const langPrefix = localeConfig.generateLanguagePrefix(i18n.language);

  const {
    USER_PROFILE_URL,
    USER_CONTENT_URL,
    LOGIN_CLIENT_ENV,
    LOGIN_SIGN_OUT_LINK,
  } = useContext(ConfigContext);

  let accountsUrl = 'https://accounts.autodesk.com';
  if (LOGIN_CLIENT_ENV !== 'prod') {
    accountsUrl = 'https://accounts-staging.autodesk.com';
  }

  const user = useUserProfile({
    oxygenId: userId,
    bypassCache: false,
    userProfileURL: USER_PROFILE_URL,
    userContentURL: USER_CONTENT_URL,
  });

  const profileLinkHref = user.data.encryptedId
    ? `/autodesk-university${langPrefix}/au-online/profile?code=${encodeURIComponent(
        user.data.encryptedId
      )}`
    : '';

  const autodeskUniversityList: UserMenuListItem[] = [
    // Show a skeleton loading effect if profile is still loading
    ...(user.isLoading
      ? [
          {
            text: '',
            link: '',
            isLoading: true,
          },
        ]
      : []),
    // Show profile link only if encryptedId is available to avoid "undefined" in the URL
    ...(profileLinkHref && !user.isLoading
      ? [
          {
            link: profileLinkHref,
            text: t('userMenu.publicProfile'),
          },
        ]
      : []),
    {
      link: `${accountsUrl}/Profile/EditProfile`,
      text: t('userMenu.editAccount'),
    },
    {
      link: `/autodesk-university${langPrefix}/my-au`,
      text: t('userMenu.myAu'),
    },
  ];

  const autodeskAccountList: UserMenuListItem[] = [
    {
      link: 'https://manage.autodesk.com/cep/',
      text: t('userMenu.manageProductsDownloads'),
    },
    {
      link: `${accountsUrl}/Profile/Security`,
      text: t('userMenu.securitySettings'),
    },
    {
      text: t('userMenu.signOut'),
      link: LOGIN_SIGN_OUT_LINK || undefined,
      onClick: !LOGIN_SIGN_OUT_LINK ? logoutOnClickOverride : undefined,
    },
  ];

  const menuGroups: MenuGroup[] = [
    { list: autodeskUniversityList, title: t('userMenu.autodeskUniversity') },
    { list: autodeskAccountList, title: t('userMenu.autodeskAccount') },
  ];

  return (
    <div className="au-relative au-bg-white">
      <div className="au-flex au-max-h-[360px] au-min-w-[200px] au-p-2 au-overflow-y-auto">
        <div className="au-p-c15">
          {menuGroups.map(menuGroup => (
            <div key={menuGroup.title} className="menu-wrapper">
              <h2 className="user-menu-title">{menuGroup.title}</h2>
              <ul className="au-m-0 au-p-0 au-text-left au-mt-c14 au-flex au-flex-col au-gap-c14">
                {menuGroup.list.map(item => (
                  <li key={item.text}>
                    <MenuLink {...item} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
